/** Frozen device setup and original speech admission. No financial executor.
 * The host calls recovery explicitly; reload never reacquires a microphone.
 */
const copy = (value) => JSON.parse(JSON.stringify(value));
export const voiceJournalKey = (owner, page) => `alloficient.agent.voice.${encodeURIComponent(owner)}.${page || 'general'}`;

export class VoiceIntake {
  constructor({ owner, currentOwner, page, api, storage = window.sessionStorage, random = window.crypto }) {
    Object.assign(this, { owner, currentOwner, api, storage, random });
    this.key = voiceJournalKey(owner, page);
  }

  requireOwner() {
    if (!this.owner || this.currentOwner() !== this.owner) throw new Error('Voice owner changed. Reconcile after signing in again.');
  }

  read() {
    this.requireOwner();
    const raw = this.storage.getItem(this.key);
    if (!raw) return null;
    let value;
    try { value = JSON.parse(raw); }
    catch (_) { throw new Error('The saved voice request needs reconciliation. It was not discarded.'); }
    if (!value || value.version !== 1 || value.owner !== this.owner || !value.request?.request_key || !Array.isArray(value.inputs)) {
      throw new Error('The saved voice request needs reconciliation. It was not discarded.');
    }
    return value;
  }

  save(value) {
    this.requireOwner();
    this.storage.setItem(this.key, JSON.stringify(value));
    return value;
  }

  update(key, change) {
    const value = this.read();
    if (!value || value.request.request_key !== key) throw new Error('That voice generation is no longer current.');
    change(value);
    return this.save(value);
  }

  acceptSession(current, session) {
    const receipt = this.sessionReceipt(session);
    if (current.session?.session_id && current.session.session_id !== receipt.session_id) {
      throw new Error('The voice request returned another call identity.');
    }
    const priorRevision = current.session?.state_revision;
    const revisionKnown = Number.isInteger(priorRevision) && Number.isInteger(receipt.state_revision);
    if (revisionKnown && receipt.state_revision < priorRevision) return;
    if (current.session?.status === 'ended' && receipt.status !== 'ended'
        && (!revisionKnown || receipt.state_revision <= priorRevision)) return;
    if (current.endRequested && ['creating', 'active', 'uncertain'].includes(receipt.status)) {
      // Keep a newly discovered ID for End, but a stale acknowledgment cannot
      // revive the local call or replace a confirmed closure.
      if (!current.session) current.session = { ...receipt, status: 'ending' };
      return;
    }
    if (!revisionKnown && current.session?.status === 'ended' && !current.session.error_code && receipt.error_code) return;
    current.session = receipt;
  }

  async createSession({ sdp, threadId = null, continuation = null }) {
    if (this.read()) throw new Error('Reconcile the saved voice call before starting another.');
    const bytes = this.random.getRandomValues(new Uint8Array(32));
    const capability = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
    const request = { request_key: this.random.randomUUID(), device_id: this.random.randomUUID(), thread_id: threadId, sdp };
    if (continuation) request.continuation = copy(continuation);
    this.save({ version: 1, owner: this.owner, capability, request, inputs: [], endRequested: false, session: null });
    try {
      const response = await this.api.create(request, capability);
      this.update(request.request_key, (value) => {
        this.acceptSession(value, response.data);
      });
      if (this.read().endRequested) {
        await this.end();
        throw new Error('Voice setup ended before capture started.');
      }
      return response.data; // SDP is ephemeral; it is not copied into messages or the receipt.
    } catch (error) {
      if (error.response?.data?.session && this.currentOwner() === this.owner) {
        this.update(request.request_key, (value) => { this.acceptSession(value, error.response.data.session); });
      }
      throw error; // Retain the frozen identity; never recreate an unknown call.
    }
  }

  sessionReceipt(session) {
    return { session_id: session.session_id, thread_id: session.thread_id, status: session.status,
      state_revision: session.state_revision,
      renew_after_ms: session.renew_after_ms, continuation: session.continuation,
      expires_at: session.expires_at, provider_deadline_at: session.provider_deadline_at, error_code: session.error_code };
  }

  async recover() {
    const value = this.read();
    if (!value) return null;
    const response = await this.api.request(value.request.request_key, value.capability);
    return this.update(value.request.request_key, (current) => { this.acceptSession(current, response.data); });
  }

  async end() {
    let value = this.read();
    if (!value) return null;
    value = this.update(value.request.request_key, (current) => { current.endRequested = true; });
    // The frozen End can arrive before setup reservation itself. A server
    // tombstone, rather than an inconclusive GET404, fences that late request.
    const response = value.session?.session_id
      ? await this.api.end(value.session.session_id, value.capability)
      : await this.api.endRequest(copy(value.request), value.capability);
    this.update(value.request.request_key, (current) => { this.acceptSession(current, response.data); });
    return response.data;
  }

  async reconcileInputs() {
    const value = this.read();
    if (!value?.session?.session_id) return this.recover();
    const response = await this.api.inspect(value.session.session_id, value.capability);
    return this.update(value.request.request_key, (current) => {
      this.acceptSession(current, response.data);
      for (const item of current.inputs) {
        const accepted = response.data.inputs.find((row) => row.item_id === item.itemId);
        if (accepted) {
          if (accepted.message !== item.payload.message || accepted.previous_item_id !== item.payload.previous_item_id
              || accepted.source_previous_item_id !== item.payload.source_previous_item_id
              || JSON.stringify(accepted.attachment_ids) !== JSON.stringify(item.payload.attachment_ids)) {
            throw new Error('The accepted voice input conflicts with its saved original.');
          }
          item.receipt = copy(accepted);
        } else if (['ended', 'failed'].includes(response.data.status)) {
          item.notAccepted = true; // Retain as a draft, never describe it as submitted.
        }
      }
    });
  }

  releaseUnaccepted(itemId) {
    // Explicit host action only, after the owner discarded it or the host saved
    // its complete payload as a draft. Return/read alone never erases speech.
    const value = this.read();
    const item = value?.inputs.find((row) => row.itemId === itemId);
    if (!item?.notAccepted || item.receipt) throw new Error('That input has not been proven unsubmitted.');
    this.update(value.request.request_key, (current) => { current.inputs = current.inputs.filter((row) => row.itemId !== itemId); });
  }

  retainUnsubmittedSpeech(items = []) {
    const saved = this.read();
    if (!saved) return false;
    return this.update(saved.request.request_key, (value) => {
      const drafts = value.captureDrafts || [];
      for (const item of items) {
        if (typeof item.transcript !== 'string' || !item.transcript.trim()
            || value.inputs.some((row) => row.itemId === item.itemId)
            || drafts.some((row) => row.itemId === item.itemId)) continue;
        drafts.push({ itemId: item.itemId, transcript: item.transcript });
      }
      value.captureDrafts = drafts;
    });
  }

  releaseSpeechDraft(itemId) {
    const saved = this.read();
    if (!saved?.endRequested) throw new Error('End capture before discarding an unsent draft.');
    this.update(saved.request.request_key, (value) => {
      value.captureDrafts = (value.captureDrafts || []).filter((row) => row.itemId !== itemId);
    });
  }

  async heartbeat() {
    const value = this.read();
    if (!value?.session?.session_id || value.endRequested) throw new Error('This voice call is ending.');
    const response = await this.api.heartbeat(value.session.session_id, value.capability);
    return this.update(value.request.request_key, (current) => { this.acceptSession(current, response.data); }).session;
  }

  async admit(input, { context = {}, attachmentIds = [] } = {}) {
    let value = this.read();
    if (!value?.session?.session_id || input.sessionId !== value.session.session_id) throw new Error('Voice input belongs to another call.');
    let frozen = value.inputs.find((row) => row.itemId === input.itemId);
    if (frozen) {
      if (frozen.payload.message !== input.transcript
          || frozen.payload.previous_item_id !== input.previousAdmittedItemId
          || frozen.payload.source_previous_item_id !== input.sourcePreviousItemId) {
        throw new Error('The original spoken input changed. Its identity cannot be reused.');
      }
      // Live page values or a later upload must not change an old retry.
      if (frozen.receipt) return frozen.receipt;
    } else {
      if (value.endRequested) throw new Error('The ended call cannot admit new speech.');
      frozen = { itemId: input.itemId, payload: copy({ item_id: input.itemId,
        previous_item_id: input.previousAdmittedItemId, source_previous_item_id: input.sourcePreviousItemId,
        message: input.transcript, attachment_ids: attachmentIds, context,
        ...(input.transcriptionObservation ? { transcription_observation: input.transcriptionObservation } : {}) }), receipt: null };
      value = this.update(value.request.request_key, (current) => { current.inputs.push(frozen); });
    }
    const response = await this.api.admit(value.session.session_id, copy(frozen.payload), value.capability);
    this.update(value.request.request_key, (current) => {
      current.inputs.find((row) => row.itemId === input.itemId).receipt = copy(response.data);
    });
    return response.data;
  }

  clearReconciled() {
    const value = this.read();
    if (!value) return;
    if (!['ended', 'failed'].includes(value.session?.status) || value.inputs.some((row) => !row.receipt) || value.captureDrafts?.length) {
      throw new Error('Unconfirmed voice work is still saved. Reconcile it before continuing.');
    }
    this.storage.removeItem(this.key);
  }
}
